import {
  FinishedUnaryCall,
  RpcError,
  RpcStatus,
} from '@protobuf-ts/runtime-rpc'
import type { Info } from 'shared/gen/pkg/proto/response'
import { Status } from 'shared/gen/pkg/proto/response'
import {
  GuaranteedResponse,
  RequiredK2Response,
} from 'shared/services/grpc/k2-use-case'
import { ErrorOptions as LoggerOptions } from 'shared/services/logger'
import {
  GrpcStatusCode,
  Metadata,
} from 'shared/services/server-action/server-action.schema'
import { isString } from '../type-guards'
import { AnyObject, Overwrite } from '../types/type-utils'
import {
  isSystemError,
  isUserError,
  SystemError,
  UserError,
} from './application-error'
import { makeErrorFromRpcError } from './rpc.utils'

type AllowedLoggerOptions = Partial<Omit<LoggerOptions, 'key' | 'feature'>>

type Response<T extends AnyObject> = T
export type K2ResponseType<T extends AnyObject> = Response<T> & { info?: Info }

/**
 * This method converts a grpc response to an error taking key properties from the response and setting the message to the detail or info.message
 * @deprecated # use `ApplicationError` instead
 */
export class GenericError extends Error {
  private _method?: string
  private _serviceName?: string
  private _status: RpcStatus
  private info?: Info
  private response?: any

  // This checks if the response had a handled message
  public hasMessage: boolean

  // Whether the error was handled by k2. It also lets us skip reporting the error.
  public handled: boolean

  // Whether the error should be reported, an additional override to tell our logger to ignore the error.
  // This is different than a "Handled" error, because that indicates that the message is user-friendly
  // this setting allows us to skip reporting the error, while still considering it to be handled or unhandled.
  public doNotLog: boolean

  // this is used to store any additional information that we want to log with the error
  public loggerOptions?: AllowedLoggerOptions

  // This is used to store any additional information that we want to return to the client
  public metadata?: Metadata

  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    if (results instanceof GenericError) {
      super(results.message, options)
      this._method = results._method
      this._serviceName = results._serviceName
      this._status = results._status
      this.info = results.info
      this.response = results.response
      this.hasMessage = results.hasMessage
      this.handled = results.handled
      this.loggerOptions = results.loggerOptions
      this.metadata = results.metadata
    } else {
      const statusCode =
        (results.status?.code as GrpcStatusCode) || GrpcStatusCode.UNKNOWN
      const isRpcError = results?.response instanceof RpcError
      super(isRpcError ? results.response.message : '', options)
      this._method = results.method?.name
      this._serviceName = results.method?.service?.typeName
      this._status = results?.status || { code: 'UNKNOWN', detail: '' }
      this.info = results.response?.info
      this.response = results.response
      this.hasMessage =
        !!results.response?.info?.message &&
        results.response?.info?.message !== ''
      this.handled = this.hasMessage || isHandledGrpcCodeStatus(statusCode)
      this.message = extractMessage(results)
      this.loggerOptions = this.generateLoggerOptions()
      this.metadata = {
        grpcStatusCode: statusCode,
      }
    }

    // by default, k2 logs every response and status code, so we don't need to double log it
    this.doNotLog = true

    this.name = 'GenericError'
  }

  toRpcError(): RpcError {
    const rpc = new RpcError(this.message, this._status.code)
    rpc.serviceName = this.serviceName
    rpc.methodName = this.method
    rpc.stack = this.stack

    return rpc
  }

  toApplicationError(userMessage?: string): SystemError | UserError {
    if (this.handled) {
      return new UserError(this.message, {
        handled: true,
        originalError: this,
        grpcStatusCode: this.metadata?.grpcStatusCode,
        method: this.method,
        service: this.serviceName,
        userMessage,
      })
    }

    const e = makeErrorFromRpcError(this.toRpcError())
    if (isSystemError(e)) {
      e.userMessage = userMessage || e.message
      e.handled = isString(userMessage)
    }

    return e
  }

  generateLoggerOptions(): AllowedLoggerOptions {
    if (this.handled) {
      return {}
    }

    return {
      tags: {
        methodName: this._method || 'UnknownMethod',
        serviceName: this._serviceName || 'UnknownService',
      },
    }
  }

  get status(): string {
    if (this._status?.code !== 'OK') return this._status.code

    if (this.info?.status && Object.values(Status).includes(this.info.status)) {
      return Status[this.info.status]
    }

    return 'UNKNOWN'
  }

  get detail(): string {
    const rpcMessage = isString(this.response)
      ? this.response
      : this._status?.detail

    const k2Message = this.info?.message

    return rpcMessage || k2Message || 'No error message provided'
  }

  get method(): string {
    return this._method || 'UnknownMethod'
  }

  get serviceName(): string {
    return this._serviceName || 'UnknownService'
  }

  override toString(): string {
    return `${this.name}(${this.status}): ${this.serviceName}.${this.method} - ${this.detail}`
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class UnknownError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'UNKNOWN',
      detail: 'Unknown Error',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }

    super({ status, ...results }, options)
    this.name = 'UnknownError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class CancelledError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    super({ status: { code: 'CANCELLED', detail: '' }, ...results }, options)
    this.name = 'CancelledError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class InvalidArgumentError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'INVALID_ARGUMENT',
      detail: 'Invalid Argument',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'InvalidArgumentError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class DeadlineExceededError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'DEADLINE_EXCEEDED',
      detail: 'Deadline Exceeded',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'DeadlineExceededError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class NotFoundError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'NOT_FOUND',
      detail: 'Not Found',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'NotFoundError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class AlreadyExistsError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'ALREADY_EXISTS',
      detail: 'Already Exists',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'AlreadyExistsError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class PermissionDeniedError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'PERMISSION_DENIED',
      detail: 'Permission Denied',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'PermissionDeniedError'
    this.handled = true
    this.doNotLog = true

    if (!this.message || this.message === '') {
      this.message = 'Permission Denied'
    }
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class ResourceExhaustedError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'RESOURCE_EXHAUSTED',
      detail: 'Resource Exhausted',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'ResourceExhaustedError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class FailedPreconditionError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'FAILED_PRECONDITION',
      detail: 'Failed Precondition',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'FailedPreconditionError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class AbortedError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'ABORTED',
      detail: 'Aborted',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'AbortedError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class OutOfRangeError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'OUT_OF_RANGE',
      detail: 'Out of Range',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'OutOfRangeError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class UnimplementedError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'UNIMPLEMENTED',
      detail: 'Unimplemented',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'UnimplementedError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class InternalError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'INTERNAL',
      detail: 'Internal',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'InternalError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class UnavailableError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'UNAVAILABLE',
      detail: 'Unavailable',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'UnavailableError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class DataLossError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'DATA_LOSS',
      detail: 'Data Loss',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }
    super({ status, ...results }, options)
    this.name = 'DataLossError'
  }
}

/**
 * @deprecated # use `ApplicationError` instead
 */
export class UnauthenticatedError extends GenericError {
  constructor(
    results: Partial<RequiredK2Response<any>> | GenericError,
    options?: ErrorOptions,
  ) {
    const status: RpcStatus = {
      code: 'UNAUTHENTICATED',
      detail: 'Unauthenticated',
    }

    if (results instanceof GenericError && results.detail !== '') {
      status.detail = results.detail
    }

    super(
      {
        status,
        ...results,
      },
      options,
    )
    this.name = 'UnauthenticatedError'
  }
}

export function isK2Error(err: unknown): err is GenericError {
  return err instanceof GenericError
}

export function isPermissionDenied(error: unknown): boolean {
  if (
    isK2Error(error) &&
    error.metadata?.grpcStatusCode === GrpcStatusCode.PERMISSION_DENIED
  ) {
    return true
  }

  if (
    isUserError(error) &&
    error.metadata?.grpcStatusCode &&
    error.metadata.grpcStatusCode === GrpcStatusCode.PERMISSION_DENIED
  ) {
    return true
  }

  return false
}

function isHandledGrpcCodeStatus(code: GrpcStatusCode): boolean {
  const validHandledCodes: Partial<Record<GrpcStatusCode, boolean>> = {
    [GrpcStatusCode.PERMISSION_DENIED]: true,
    [GrpcStatusCode.UNAUTHENTICATED]: true,
  }
  return !!validHandledCodes[code]
}

/**
 * This method checks if a completed grpc call meets two criteria:
 * - results.status.code is "OK"
 * - results.response.info.status is equal to Status.SUCCESS
 *
 * any other response types will be returned as false
 */
export function isOK<
  T extends Pick<FinishedUnaryCall<any, any>, 'status' | 'response'>,
>(results: Partial<T>): results is SuccessfulResponse<T> {
  return (
    results?.status?.code === 'OK' &&
    results?.response?.info?.status === Status.SUCCESS
  )
}

export type K2Response<T extends FinishedUnaryCall<any, any>['response']> =
  Promise<GuaranteedResponse<T>>

/**
 * This type ensures that the response has a status of OK and a info status of SUCCESS
 */
export type SuccessfulResponse<
  T extends Pick<FinishedUnaryCall<any, any>, 'status' | 'response'>,
> = T & {
  status: Overwrite<FinishedUnaryCall<any, any>['status'], { code: 'OK' }>
  response: GuaranteedResponse<T['response']>
}

function extractMessage(results: Partial<RequiredK2Response<any>>): string {
  const isRpcError = results?.response instanceof RpcError

  return isRpcError
    ? results.response.message
    : results.response?.info?.message || results.status?.detail
}
